import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChunkMeta, SemanticHit, WorkerResponse, Stage } from './types';

export type SemanticStatus = 'off' | 'loading' | 'ready' | 'error';

export interface SemanticResult extends SemanticHit { meta: ChunkMeta }

export interface SemanticState {
  status: SemanticStatus;
  /** Download progress of the current stage (bytes). */
  progress: { stage: Stage; loaded: number; total: number } | null;
  error: string | null;
  /** Totals reported by the worker once ready. */
  info: { chunks: number; model: string; bytes: number; ms: number } | null;
  results: SemanticResult[] | null;
  /** Query the results belong to. */
  resultsFor: string;
  searching: boolean;
  lastMs: number | null;
}

const BASE = `${import.meta.env.BASE_URL.replace(/\/$/, '')}/semantic`;
const TOP_K = 20;
const DEBOUNCE_MS = 450;

/**
 * Owns the semantic-search worker. Nothing is downloaded until `enabled` becomes true;
 * the worker (and its ~40 MB of model/runtime/index) is created once and kept.
 */
export function useSemanticSearch(enabled: boolean, query: string): SemanticState {
  const workerRef = useRef<Worker | null>(null);
  const metaRef = useRef<ChunkMeta[] | null>(null);
  const reqId = useRef(0);
  const [status, setStatus] = useState<SemanticStatus>('off');
  const [progress, setProgress] = useState<SemanticState['progress']>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<SemanticState['info']>(null);
  const [results, setResults] = useState<SemanticResult[] | null>(null);
  const [resultsFor, setResultsFor] = useState('');
  const [searching, setSearching] = useState(false);
  const [lastMs, setLastMs] = useState<number | null>(null);

  // Create the worker on first enable
  useEffect(() => {
    if (!enabled || workerRef.current) return;
    setStatus('loading');
    setError(null);
    let cancelled = false;
    (async () => {
      try {
        const mf = await fetch(`${BASE}/manifest.json`, { cache: 'no-cache' });
        if (!mf.ok) throw new Error(`manifest.json: HTTP ${mf.status}`);
        const manifest = (await mf.json()) as { meta: { file: string; sha256: string } };
        const res = await fetch(`${BASE}/${manifest.meta.file}?v=${manifest.meta.sha256.slice(0, 12)}`, { cache: 'force-cache' });
        if (!res.ok) throw new Error(`meta.json: HTTP ${res.status}`);
        metaRef.current = (await res.json()) as ChunkMeta[];
      } catch (e) {
        if (!cancelled) { setStatus('error'); setError(e instanceof Error ? e.message : String(e)); }
        return;
      }
      if (cancelled) return;
      const w = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
      workerRef.current = w;
      w.onmessage = (ev: MessageEvent<WorkerResponse>) => {
        const m = ev.data;
        if (m.type === 'progress') setProgress({ stage: m.stage, loaded: m.loaded, total: m.total });
        else if (m.type === 'ready') { setInfo({ chunks: m.chunks, model: m.model, bytes: m.bytes, ms: m.ms }); setProgress(null); setStatus('ready'); }
        else if (m.type === 'result') {
          if (m.id !== reqId.current) return; // stale
          const meta = metaRef.current || [];
          setResults(m.hits.filter(h => meta[h.i]).map(h => ({ ...h, meta: meta[h.i] })));
          setLastMs(m.ms);
          setSearching(false);
        } else if (m.type === 'error') { setStatus('error'); setError(m.message); setSearching(false); }
      };
      w.onerror = (ev) => { setStatus('error'); setError(ev.message || 'worker error'); setSearching(false); };
      w.postMessage({ type: 'init', base: BASE });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Debounced query
  const run = useCallback((q: string) => {
    const w = workerRef.current;
    if (!w) return;
    const id = ++reqId.current;
    setSearching(true);
    setResultsFor(q);
    w.postMessage({ type: 'query', id, text: q, k: TOP_K });
  }, []);

  useEffect(() => {
    if (!enabled || status !== 'ready') return;
    const q = query.trim();
    if (q.length < 3) { setResults(null); setResultsFor(''); setSearching(false); reqId.current++; return; }
    const t = setTimeout(() => run(q), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [enabled, status, query, run]);

  return { status: enabled ? status : 'off', progress, error, info, results: enabled ? results : null, resultsFor, searching, lastMs };
}
